import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { getLocationData, updateManMadeHeritageData, deleteManMadeHeritageData, getManMadeHeritageRecordData } from '../../../../services/Location';
import { DESIRED_COLUMNS } from './constants';
import { showDeletedToast, showErrorToast, showInfoToast, showSuccessToast } from '../../../../util/errorHandler';


export const useManMadeHeritageData = (alias) => {
  const [landingPageData, setLandingPageData] = useState();
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      if (alias) {
        const result = await getLocationData(alias, 'bodyofwork', 'man-made-heritage');
        
        if (result?.data?.length > 0) {
          setLandingPageData(result);

          // Set the original column headers for reference
          const keys = Object.keys(result.data[0]);
          setColumnHeaders(keys);

          // Sort data by sr_no first
          const sortedData = result.data?.sort((a, b) => {
            const srNoA = parseInt(a.sr_no) || 0;
            const srNoB = parseInt(b.sr_no) || 0;
            return srNoA - srNoB;
          });

          // Create row data for the table from sorted data
          const rowData = sortedData?.map((item) => {
            const row = DESIRED_COLUMNS.map((key) => {
              const value = item[key] ?? "";
              return value;
            });
            return row;
          });
          
          setTableData(rowData);
        }
      }
    } catch (error) {
      // Error is handled by the UI state (isLoading will be false)
    } finally {
      setIsLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    fetchData();
  }, [fetchData, quickSave]);

  const refreshData = useCallback(() => {
    setQuickSave(prev => !prev);
  }, []);

  return {
    landingPageData,
    columnHeaders,
    tableData,
    isLoading,
    refreshData
  };
};

export const useEditModal = (alias, refreshData) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [formRef, setFormRef] = useState(null);
  const toast = useToast();

  const fetchRecordData = useCallback(async (recordAlias) => {
    try {
      setIsEditLoading(true);
      const result = await getManMadeHeritageRecordData(alias, recordAlias);
      
      if (result?.status === 200 && result?.data && result.data.length > 0) {
        setEditData(result.data[0]);
        setSelectedRecord(result.data[0]);
        setEditModalOpen(true);
      } else {
        throw new Error('No data found');
      }
    } catch (error) {
            showErrorToast(toast, "Failed to fetch record data");
    } finally {
      setIsEditLoading(false);
    }
  }, [alias, toast]);

  const handleEditSubmit = useCallback(async (formData) => {
    try {
      setIsEditSubmitting(true);
      
      // Use the same API call as the HotTable save function
      const result = await updateManMadeHeritageData(alias, [formData]);
      
      if (result?.status === 200) {
                showSuccessToast(toast, "Record updated successfully");
        
        // Refresh the table data
        refreshData();
        
        setEditModalOpen(false);
        setEditData({});
        setSelectedRecord(null);
      } else {
        throw new Error('Update failed');
      }
      
    } catch (error) {
            showErrorToast(toast, "Failed to update record");
    } finally {
      setIsEditSubmitting(false);
    }
  }, [alias, refreshData, toast]);

  const closeModal = useCallback(() => {
    setEditModalOpen(false);
    setEditData({});
    setSelectedRecord(null);
  }, []);

  return {
    editModalOpen,
    editData,
    isEditLoading,
    isEditSubmitting,
    selectedRecord,
    formRef,
    setFormRef,
    fetchRecordData,
    handleEditSubmit,
    closeModal
  };
};

export const useTableSave = (alias, landingPageData, refreshData) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteValues, setDeleteValues] = useState([]);
  const originalDataRef = useRef(null);
  const toast = useToast();

  // Update original data ref when landingPageData changes
  useEffect(() => {
    if (landingPageData?.data) {
      originalDataRef.current = landingPageData.data.map(item => ({ ...item }));
    }
  }, [landingPageData]);

  // Helper function to find original record by alias or _id
  const findOriginalRecord = useCallback((aliasValue, _idValue) => {
    if (!originalDataRef.current) return null;
    return originalDataRef.current.find(
      item => (aliasValue && item.alias === aliasValue) || (_idValue && item._id === _idValue)
    );
  }, []);

  // Helper function to check if data has changed
  const hasDataChanged = useCallback((original, current) => {
    if (!original) return true; // New record
    return DESIRED_COLUMNS.some(column => {
      const originalValue = original[column] ?? "";
      const currentValue = current[column] ?? "";
      return String(originalValue) !== String(currentValue);
    });
  }, []);

  const saveClickCallback = useCallback(async (tableData) => {
    // Filter out the Actions column and empty rows
    const formattedData = tableData
      ?.filter(row => row.some((cell, index) => index < DESIRED_COLUMNS.length && cell !== null && cell !== ''))
      ?.map(row => {
        const rowData = {};
        DESIRED_COLUMNS.forEach((columnName, index) => {
          if (row[index] !== null && row[index] !== '') {
            rowData[columnName] = row[index];
          }
        });
        return rowData;
      });

    // Find only changed records by comparing with original data
    const changedData = formattedData
      .map(currentRow => {
        const originalRecord = findOriginalRecord(currentRow.alias, currentRow._id);
        if (hasDataChanged(originalRecord, currentRow)) {
          // Include _id from original record if it exists (for updates)
          if (originalRecord?._id) {
            return { ...currentRow, _id: originalRecord._id };
          }
          // For new records, return as is
          return currentRow;
        }
        return null;
      })
      .filter(item => item !== null);

    // Check if there are any changes to save
    if (changedData?.length === 0) {
            showInfoToast(toast, "No changes to save");
      return;
    }

    if (changedData.length > 0) {
      setIsSubmitting(true);
      try {
        const result = await updateManMadeHeritageData(alias, changedData);

        if (result?.status === 200) {
          refreshData();
                    showSuccessToast(toast, "Data saved successfully");
        }
      } catch (error) {
                showErrorToast(toast, "Failed to save data. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    }
  }, [alias, refreshData, toast, findOriginalRecord, hasDataChanged]);

  const handleDelete = useCallback(async (recordAlias) => {
    if (!recordAlias) return;

    // Find the record in original data
    const recordToDelete = originalDataRef.current?.find(
      item => item.alias === recordAlias
    );

    if (!recordToDelete) {
            showErrorToast(toast, "Record not found");
      return;
    }

    // Add to deleteValues if not already there
    setDeleteValues(prev => {
      const exists = prev.some(item => item._id === recordToDelete._id || item.alias === recordAlias);
      if (exists) return prev;
      return [...prev, { _id: recordToDelete._id }];
    });

    // Call delete API immediately
    try {
      const result = await deleteManMadeHeritageData(alias, [{ _id: recordToDelete._id }]);

        if (result?.status === 200) {
                    showDeletedToast(toast);
          // Refresh data after successful delete
          refreshData();
        }
      } catch (error) {
                showErrorToast(toast, "Failed to delete record. Please try again.");
        // Remove from deleteValues if delete failed
        setDeleteValues(prev => prev.filter(item => item._id !== recordToDelete._id));
    }
  }, [alias,  refreshData, toast]);

  return {
    isSubmitting,
    saveClickCallback,
    handleDelete,
    deleteValues
  };
};
